import { Component, OnInit } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterEvent } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay, tap } from 'rxjs/operators';

import { GetCollectionsQuery, GetCollectionsQueryVariables } from './common/generated-types';
import { GET_COLLECTIONS } from './common/graphql/documents.graphql';
import { DataService } from './core/providers/data/data.service';
import { AppCurrency, CurrencyService } from './core/providers/currency/currency.service';
import { AppLanguage, LanguageService } from './core/providers/language/language.service';
import { StateService } from './core/providers/state/state.service';
import { GET_EXCHANGE_RATE } from './shared/pipes/get-exchange-rate.graphql';

@Component({
    selector: 'vsf-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
    cartDrawerVisible$: Observable<boolean>;
    mobileNavVisible$: Observable<boolean>;
    isHomePage$: Observable<boolean>;
    navigating = false;
    topCollections$: Observable<GetCollectionsQuery['collections']['items']>;
    currentLanguage: AppLanguage = 'en';
    currentCurrency: AppCurrency = 'USD';
    sypAvailable$: Observable<boolean>;

    navigation = {};

    constructor(private router: Router,
                private stateService: StateService,
                private dataService: DataService,
                private languageService: LanguageService,
                private currencyService: CurrencyService,
                private translate: TranslateService) {
        this.sypAvailable$ = this.dataService.query<{ exchangeRate: number }>(GET_EXCHANGE_RATE).pipe(
            map(data => Number(data?.exchangeRate) > 0),
            catchError(() => of(false)),
            tap(available => {
                if (!available && this.currencyService.currentCurrency === 'SYP') {
                    this.currencyService.setCurrency('USD');
                }
            }),
            shareReplay(1),
        );
    }

    ngOnInit(): void {
        this.languageService.initLanguage();
        this.currentLanguage = this.languageService.currentLanguage;
        this.currentCurrency = this.currencyService.currentCurrency;
        this.translate.use(this.languageService.currentLanguage);

        this.cartDrawerVisible$ = this.stateService.select(state => state.cartDrawerOpen);
        this.mobileNavVisible$ = this.stateService.select(state => state.mobileNavMenuIsOpen);
        this.isHomePage$ = this.router.events.pipe(
            filter<any>(event => event instanceof RouterEvent),
            map((event: RouterEvent) => event.url === '/' || event.url.startsWith('/#')),
        );
        this.topCollections$ = this.dataService.query<GetCollectionsQuery, GetCollectionsQueryVariables>(GET_COLLECTIONS, {
            options: {take: 25, topLevelOnly: true}
        }).pipe(
            map(({collections}) => collections.items)
        );
        this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                this.navigating = event.url.startsWith('/account');
            } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
                this.navigating = false;
            }
        });
    }

    openCartDrawer() {
        this.stateService.setState('cartDrawerOpen', true);
    }

    closeCartDrawer() {
        this.stateService.setState('cartDrawerOpen', false);
    }

    setLanguage(lang: AppLanguage): void {
        this.languageService.setLanguage(lang);
    }

    setCurrency(currency: AppCurrency): void {
        this.currencyService.setCurrency(currency);
    }
}
